import client from 'braintree-web/client';
import dataCollector from 'braintree-web/data-collector';
import paypal from 'braintree-web/paypal';
import threeDSecure from 'braintree-web/three-d-secure';
import find from 'lodash/find';
import get from 'lodash/get';
import { CURRENCY_CONFIG, MERCHANT_NAME } from '../brands/default/config';
import {
  PAYMENT_METHODS,
  PAYMENT_TYPES,
  BRAINTREE,
} from '../brands/default/constants';
import {
  ERROR_MESSAGE,
  LIABILITY_ERROR,
  NO_EXPRESS_BUY,
  TRY_CHECKOUT,
  GENERIC_ERROR,
  PROGRESS_MESSAGE_WAIT,
  PROGRESS_MESSAGE_PAY,
  PROGRESS_MESSAGE_SUCCESS,
  PROGRESS_MESSAGE_FAILED,
} from '../brands/default/content/helpers';
import { trackPurchase } from './analytics';
import BraintreeService from './braintreeService';
import getPaymentOptions from './getPaymentOptions';
import { getGateway } from './payment';
import { getProductTotal } from './productsService';
import { trackNewVouchers } from './vouchers';

const currencyCode = CURRENCY_CONFIG.code;

const giftsExpressBuyService = {
  cardDefault: null,
  currency: currencyCode,
  deviceFingerprintId: '',
  failToCheckout: null,
  instrumentToUse: null,
  isBraintree: false,
  products: [],
  quantity: 0,
  setProgressTest: null,
  success: null,
  threeDSecureInstance: null,
  threeDSEnabled: false,
  tokenizationPayload: {},
  useBraintree: false,
};

const goToCheckout = (message) => {
  giftsExpressBuyService.failToCheckout(message || TRY_CHECKOUT);
};

giftsExpressBuyService.isCvvRequired = (response) => {
  let cvvRequired = false;

  if (!response) return false;

  const product = get(
    response,
    'apiUserDealInfoVO.apiDealProductsVO.theDealProducts[0]',
    null,
  );
  if (!product) return false;

  // cvv check
  giftsExpressBuyService.threeDSEnabled = response.paymentThreeDSCheckEnabled;
  const paymentCvvCheckEnabled = response.paymentCvvCheckEnabled;
  const paymentCvvCheckMaxOrderCostLimitForToday =
    response.paymentCvvCheckMaxOrderCostLimitForToday;
  const paymentCvvCheckMaxItemLimitForToday =
    response.paymentCvvCheckMaxItemLimitForToday;

  if (!paymentCvvCheckEnabled) return false;

  // cvv check user fields
  const data = response.apiUserDealInfoVO;
  giftsExpressBuyService.threeDSRequired =
    data.threeDSAuthenticationRequiredForUser;
  cvvRequired = data.paymentCvvCheckRequiredForUser;
  const initialCvvRequired = data.paymentCvvCheckRequiredForUser;
  const totalOrderCostByUserForToday = data.totalOrderCostByUserForToday;
  const totalOrderItemsByUserForToday = data.totalOrderItemsByUserForToday;

  const quantity = giftsExpressBuyService.quantity;
  const price = product.price;
  const totalAmountToPay = quantity * price;

  let cvvSuccessToday = false;
  if (
    (totalOrderCostByUserForToday > paymentCvvCheckMaxOrderCostLimitForToday ||
      totalOrderItemsByUserForToday > paymentCvvCheckMaxItemLimitForToday) &&
    initialCvvRequired === false
  ) {
    // A limit already exceeded, and CVV is false, so has already been used successfully today
    cvvSuccessToday = true;
  }

  // SHOW RULES, ALWAYS ON - over 500 (daily limit) in this order
  if (totalAmountToPay > paymentCvvCheckMaxOrderCostLimitForToday) {
    cvvRequired = true;
  }
  // DAILY LIMITS - order pushes daily total over 500, or pushes daily items over 10
  // don't check if user sucessfully challenged with cvv today
  if (
    !cvvSuccessToday &&
    (totalOrderItemsByUserForToday + quantity >
      paymentCvvCheckMaxItemLimitForToday ||
      totalOrderCostByUserForToday + totalAmountToPay >
        paymentCvvCheckMaxOrderCostLimitForToday)
  ) {
    cvvRequired = true;
  }

  return cvvRequired;
};
const setupPayPal = async (clientError, clientInstance) => {
  if (
    giftsExpressBuyService.instrumentToUse &&
    giftsExpressBuyService.instrumentToUse.type === PAYMENT_TYPES.card &&
    giftsExpressBuyService.useBraintree &&
    giftsExpressBuyService.threeDSEnabled &&
    giftsExpressBuyService.threeDSRequired
  ) {
    if (giftsExpressBuyService.threeDSecureInstance) {
      giftsExpressBuyService.threeDSecureInstance.teardown();
    }
    try {
      const threeDSecureInstance = await threeDSecure.create({
        client: clientInstance,
        version: 2,
      });
      if (threeDSecureInstance) {
        giftsExpressBuyService.threeDSecureInstance = threeDSecureInstance;
      }
    } catch {
      goToCheckout(TRY_CHECKOUT);
    }
  }
  try {
    const dataCollectorInstance = await dataCollector.create({
      client: clientInstance,
      paypal: true,
    });
    if (dataCollectorInstance) {
      // save device data for use in payment
      giftsExpressBuyService.dataCollectorInstance = dataCollectorInstance;
      // continue with payment
      await giftsExpressBuyService.continueBraintreePayment();
    }
  } catch {
    goToCheckout(ERROR_MESSAGE);
  }

  try {
    const paypalInstance = await paypal.create({
      client: clientInstance,
    });
    if (paypalInstance) {
      giftsExpressBuyService.paypalInstance = paypalInstance;
    }
  } catch {
    goToCheckout(ERROR_MESSAGE);
  }
};

const createClient = (tokenResponse) => {
  giftsExpressBuyService.clientId = tokenResponse.data.clientId;
  giftsExpressBuyService.clientToken = tokenResponse.data.clientToken;
  client.create(
    { authorization: giftsExpressBuyService.clientToken },
    setupPayPal,
  );
};

giftsExpressBuyService.setupBraintree = async () => {
  try {
    const response = await BraintreeService.getClientToken();
    if (response) {
      createClient(response);
    }
  } catch {
    goToCheckout(ERROR_MESSAGE);
  }
};

const fetchPaymentOptions = async () => {
  giftsExpressBuyService.setProgressTest(PROGRESS_MESSAGE_WAIT);
  try {
    const deal = await getPaymentOptions({
      dealId: giftsExpressBuyService.dealId,
      dealVoucherProductId: giftsExpressBuyService.dealProductId,
      quantity: giftsExpressBuyService.quantity,
    });
    if (deal) {
      giftsExpressBuyService.deal = deal;
      giftsExpressBuyService.deal.id = giftsExpressBuyService.dealId;
      const gatewayValue = getGateway(deal.paymentOptions.paymentInstruments);
      giftsExpressBuyService.useBraintree = gatewayValue === BRAINTREE;
      giftsExpressBuyService.creditBalances =
        deal.paymentOptions.creditBalances;
      if (deal.isGiftClaimable) {
        giftsExpressBuyService.isGiftClaimable = true;
      }
      if (deal.paymentDeviceProfilingEnabled) {
        giftsExpressBuyService.deviceFingerprintId =
          deal.paymentDeviceProfileVO.sessionId;
        giftsExpressBuyService.paymentDeviceProfile =
          deal.paymentDeviceProfileVO;
      }

      // Is deal gift express buyable
      // eslint-disable-next-line promise/always-return
      if (
        !giftsExpressBuyService.deal.isGiftClaimable ||
        giftsExpressBuyService.isCvvRequired(giftsExpressBuyService.deal)
      ) {
        // Bump out to checkout to allow them to buy normally
        goToCheckout(NO_EXPRESS_BUY);

        return;
      }

      giftsExpressBuyService.initiatePayment(
        giftsExpressBuyService.deal,
        giftsExpressBuyService.makePayment,
      );
    }
  } catch {
    goToCheckout(TRY_CHECKOUT);
  }
};

const getDefaultCard = async (id) => {
  try {
    const response = await BraintreeService.getVaultedCards(id);
    if (response) {
      const cards = response.data.customer.creditCards;
      const cardDefault = cards.filter((card) => card.default) || null;
      if (cardDefault !== null) {
        return cardDefault[0].token;
      }

      return null;
    }

    return null;
  } catch {
    goToCheckout(GENERIC_ERROR);

    return null;
  }
};

const finalStepPayment = async ({ responseNonce, makePayment }) => {
  const payload = await giftsExpressBuyService.threeDSecureInstance.verifyCard({
    amount: getProductTotal(giftsExpressBuyService.products)
      .toFixed(2)
      .toString(),
    merchantName: MERCHANT_NAME,
    nonce: responseNonce.data.target.nonce,
    onLookupComplete: (data, next) => {
      next();
    },
  });
  if (payload) {
    if (payload.liabilityShiftPossible) {
      goToCheckout(LIABILITY_ERROR);
    } else {
      // Liablity has not shifted and will not shift
      BraintreeService.nonce = payload.nonce;
      makePayment(BraintreeService.makePayment);
    }
  }
};

const defineCard = async ({ makePayment }) => {
  if (
    giftsExpressBuyService.threeDSEnabled &&
    giftsExpressBuyService.threeDSRequired
  ) {
    try {
      const response = await getDefaultCard(giftsExpressBuyService.clientId);
      if (response) {
        const responseNonce = await BraintreeService.getNonce(response);
        if (responseNonce && !responseNonce.data.target.nonce) {
          goToCheckout(LIABILITY_ERROR);

          return;
        }
        finalStepPayment({ makePayment, responseNonce });
      }
    } catch {
      goToCheckout(NO_EXPRESS_BUY);
    }
  } else {
    makePayment(BraintreeService.makePayment);
  }
};

const expressCheckoutBraintree = ({ total, makePayment }) => {
  giftsExpressBuyService.isBraintree = true;
  // setup Braintree
  giftsExpressBuyService.setupBraintree();
  // the callback after setting up Braintree
  giftsExpressBuyService.continueBraintreePayment = async () => {
    BraintreeService.totalAmountToPay = total;
    BraintreeService.products = giftsExpressBuyService.products;
    BraintreeService.currency = giftsExpressBuyService.currency;
    BraintreeService.deviceData =
      giftsExpressBuyService.dataCollectorInstance.deviceData;
    BraintreeService.expressBuy = true;
    BraintreeService.id = giftsExpressBuyService.dealId;
    if (giftsExpressBuyService.instrumentToUse.type === PAYMENT_TYPES.card) {
      BraintreeService.paymentMethod = PAYMENT_METHODS.card;
    }
    if (giftsExpressBuyService.instrumentToUse.type === PAYMENT_TYPES.paypal) {
      BraintreeService.paymentMethod = PAYMENT_METHODS.paypal;
    }
    defineCard({ makePayment });
  };
};

const definePaymentProcessor = ({ total, makePayment }) => {
  if (
    (giftsExpressBuyService.instrumentToUse &&
      !giftsExpressBuyService.isGiftClaimable &&
      giftsExpressBuyService.instrumentToUse.type === PAYMENT_TYPES.card &&
      giftsExpressBuyService.useBraintree) ||
    giftsExpressBuyService.instrumentToUse.type === PAYMENT_TYPES.paypal
  ) {
    expressCheckoutBraintree({ makePayment, total });
  } else {
    goToCheckout(NO_EXPRESS_BUY);
  }
};

giftsExpressBuyService.initiatePayment = async (deal, makePayment) => {
  const product = get(
    deal,
    'apiUserDealInfoList[0].apiDealProductsVO.theDealProducts[0]',
    null,
  );

  if (!product) {
    goToCheckout(TRY_CHECKOUT);

    return;
  }

  if (giftsExpressBuyService.quantity > 0) {
    product.quantity = giftsExpressBuyService.quantity;
    product.id = product.dealProductId;

    giftsExpressBuyService.products = [product];
  }

  // examine deal.paymentOptions.paymentInstruments to see which payment type default 'credit_card' or 'paypal'

  giftsExpressBuyService.instrumentToUse = find(
    deal.paymentOptions.paymentInstruments,
    {
      default: 'yes',
    },
  );

  const total = getProductTotal(giftsExpressBuyService.products);

  definePaymentProcessor({ makePayment, total });
};

giftsExpressBuyService.makePayment = async (paymentFunction) => {
  giftsExpressBuyService.setProgressTest(PROGRESS_MESSAGE_PAY);

  try {
    const order = await paymentFunction();
    if (!order.data.productOrder) {
      goToCheckout(GENERIC_ERROR);

      return false;
    }
    order.data.quantity = giftsExpressBuyService.quantity;
    order.data.walletAmountUsed = 0;
    order.data.cardType =
      get(
        giftsExpressBuyService,
        'deal.paymentOptions.paymentInstruments[0].details.brand',
      ) ?? '';
    if (giftsExpressBuyService.isBraintree) {
      order.data.cardType = PAYMENT_METHODS.paypal;
    }
    if (order.config?.data) {
      order.data.orderValue = order.config.data.cashAmount;
    }
    giftsExpressBuyService.setProgressTest(PROGRESS_MESSAGE_SUCCESS);
    order.data.currency = currencyCode;

    // Track everything purchase related
    trackPurchase([giftsExpressBuyService.deal], order, true);

    const CheckoutOrder = {
      deals: [giftsExpressBuyService.deal],
      hasNewOrder: true,
      orderId: order?.data?.productOrder?.id,
      products: giftsExpressBuyService.products,
    };
    trackNewVouchers(CheckoutOrder);

    // success - go to my  wowchers page
    giftsExpressBuyService.success(order?.data?.productOrder?.id);

    return true;
  } catch {
    giftsExpressBuyService.setProgressTest(PROGRESS_MESSAGE_FAILED);
    goToCheckout(GENERIC_ERROR);

    return false;
  }
};

giftsExpressBuyService.initExpressBuy = (deal, quantity) => {
  giftsExpressBuyService.quantity = quantity;
  giftsExpressBuyService.dealId = deal.id;
  giftsExpressBuyService.dealProductId = deal.products[0].id;
  giftsExpressBuyService.deviceFingerprintId = '';
  fetchPaymentOptions();
};
giftsExpressBuyService.initMultiExpressBuy = (deal, products) => {
  giftsExpressBuyService.products = products;
  giftsExpressBuyService.dealId = deal.id;
  giftsExpressBuyService.dealProductId = deal.products[0].id;
  giftsExpressBuyService.deviceFingerprintId = '';
  fetchPaymentOptions();
};

export default giftsExpressBuyService;
